import { type FC, type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { SegmentContext, type SegmentContextValue } from "@/contexts/SegmentContext";

export const SegmentProvider: FC<
    PropsWithChildren & {
        create?: () => SegmentContextValue;
    }
> = ({ create, children }) => {
    const context = useMemo(() => {
        if (create) {
            return create();
        }
        return undefined;
    }, [create]);

    const [platformDetails, setPlatformDetails] = useState<{ platform?: string; architecture?: string }>({});
    useEffect(() => {
        const fetchPlatformDetails = async () => {
            if (context?.platformDetails) {
                setPlatformDetails(context.platformDetails);
                return;
            }

            let platform: string | undefined;
            let architecture: string | undefined;
            try {
                if (isNavigatorUA(navigator) && navigator.userAgentData?.getHighEntropyValues) {
                    const userData = await navigator.userAgentData.getHighEntropyValues(["architecture", "platform"]);
                    architecture = userData.architecture;
                    platform = userData.platform;
                }
            } catch (error) {
                console.error("Error fetching user agent data", error);
            }

            if (!platform) {
                platform = platformFromUserAgent();
            }

            setPlatformDetails({ platform, architecture });
        };
        void fetchPlatformDetails();
    }, [context]);

    return (
        <SegmentContext.Provider
            value={{
                analytics: context?.analytics,
                platformDetails: context?.platformDetails || platformDetails,
            }}
        >
            {children}
        </SegmentContext.Provider>
    );
};

type NavigatorUA = {
    readonly userAgentData?: NavigatorUAData;
} & Navigator;
type NavigatorUAData = {
    getHighEntropyValues(hints: string[]): Promise<UADataValues>;
};
type UADataValues = {
    readonly architecture?: string;
    readonly platform?: string;
};

function isNavigatorUA(n: Navigator): n is NavigatorUA {
    return "userAgentData" in n;
}

// ported from Mixpanel browser SDK and adapted to UADataValues.platform values
function platformFromUserAgent(): string | undefined {
    const userAgent = navigator.userAgent;
    if (/Windows/i.test(userAgent)) {
        if (userAgent.includes("Phone") || userAgent.includes("WPDesktop")) {
            return "Windows Phone";
        }
        return "Windows";
    }
    if (/(iPhone|iPad|iPod)/.test(userAgent)) {
        return "iOS";
    }
    if (userAgent.includes("Android")) {
        return "Android";
    }
    if (/(BlackBerry|PlayBook|BB10)/i.test(userAgent)) {
        return "BlackBerry";
    }
    if (userAgent.includes("Mac")) {
        return "macOS";
    }
    if (userAgent.includes("Linux")) {
        return "Linux";
    }
    if (userAgent.includes("CrOS")) {
        return "Chrome OS";
    }
}
